import apiClient from "@/lib/api-client";

export interface BookCollection {
  _id?: string;
  collection_id: string;
  title: string;
  description?: string;
  cover_image_url?: string;
  status: 'draft' | 'published';
  book_ids?: string[];
  books?: any[];
  created_by?: string;
  updated_by?: string;
  published_at?: string;
  created_at?: string;
  updated_at?: string;
  book_count?: number;
}

export interface BookCollectionFilters {
  status?: 'draft' | 'published';
  search?: string;
}

export interface BookCollectionStatistics {
  total_collections: number;
  published_collections: number;
  draft_collections: number;
}

export class BookCollectionService {
  // Basic CRUD operations
  static async getCollections(filters?: BookCollectionFilters) {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.search) params.append('search', filters.search);

    const queryString = params.toString();
    const url = queryString ? `book-collection?${queryString}` : 'book-collection';
    
    const response = await apiClient.get(url);
    return response.data;
  }

  static async getCollection(id: string, includeBooks: boolean = true) {
    const params = new URLSearchParams();
    params.append('include_books', includeBooks.toString());
    
    const response = await apiClient.get(`book-collection/${id}?${params.toString()}`);
    return response.data;
  }

  static async createCollection(data: Partial<BookCollection>) {
    const response = await apiClient.post('book-collection', data);
    return response.data;
  }

  // Nhân bản thành bộ nháp mới; từng cuốn được nhân bản, file dùng chung
  static async cloneCollection(id: string, title: string) {
    const response = await apiClient.post(`book-collection/${id}/clone`, { title });
    return response.data;
  }

  static async updateCollection(id: string, data: Partial<BookCollection>) {
    const response = await apiClient.put(`book-collection/${id}`, data);
    return response.data;
  }

  static async deleteCollection(id: string) {
    const response = await apiClient.delete(`book-collection/${id}`);
    return response.data;
  }

  // Publishing operations
  static async publishCollection(id: string) {
    const response = await apiClient.patch(`book-collection/${id}/publish`);
    return response.data;
  }

  static async unpublishCollection(id: string) {
    const response = await apiClient.patch(`book-collection/${id}/unpublish`);
    return response.data;
  }

  // Status filtering
  static async getCollectionsByStatus(status: 'draft' | 'published') {
    const response = await apiClient.get(`book-collection/status/${status}`);
    return response.data;
  }

  static async getPublishedCollections() {
    const response = await apiClient.get('book-collection/published/list');
    return response.data;
  }

  // Statistics
  static async getStatistics(): Promise<{ success: boolean; data: BookCollectionStatistics }> {
    const response = await apiClient.get('book-collection/meta/statistics');
    return response.data;
  }

  // Book management within collection
  static async addBookToCollection(collectionId: string, bookId: string) {
    const response = await apiClient.post(`book-collection/${collectionId}/books`, { book_id: bookId });
    return response.data;
  }

  static async removeBookFromCollection(collectionId: string, bookId: string) {
    const response = await apiClient.delete(`book-collection/${collectionId}/books/${bookId}`);
    return response.data;
  }

  static async updateBooksOrder(collectionId: string, bookIds: string[]) {
    const response = await apiClient.patch(`book-collection/${collectionId}/books-order`, { book_ids: bookIds });
    return response.data;
  }

  /** Tiết đang ẩn riêng trong bộ: { id sách: [id section] } */
  static async getHiddenSections(collectionId: string): Promise<Record<string, string[]>> {
    const response = await apiClient.get(`book-collection/${collectionId}/section-visibility`);
    return response.data?.data?.hidden_section_ids ?? {};
  }

  /** Ẩn/hiện tiết của một cuốn chỉ trong bộ này, trả danh sách ẩn mới */
  static async setSectionsVisible(
    collectionId: string,
    bookId: string,
    sectionIds: string[],
    visible: boolean
  ): Promise<Record<string, string[]>> {
    const response = await apiClient.put(`book-collection/${collectionId}/section-visibility`, {
      book_id: bookId,
      section_ids: sectionIds,
      visible,
    });
    return response.data?.data?.hidden_section_ids ?? {};
  }

  // Search
  static async searchCollections(filters: BookCollectionFilters) {
    const response = await apiClient.post('book-collection/search', filters);
    return response.data;
  }

  // Upload cover image
  static async uploadCoverImage(file: File): Promise<{ success: boolean; data: { url: string } }> {
    try {
      const formData = new FormData();
      formData.append('files[]', file);
      
      const response = await apiClient.post('/file', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // Backend returns: { success: true, result: [...files] }
      const fileData = response.data?.result?.[0];
      if (fileData) {
        const url = fileData.url || (fileData.host + fileData.path + fileData.name);
        return {
          success: true,
          data: { url }
        };
      }
      
      throw new Error('Upload failed');
    } catch (error) {
      console.error('Cover image upload error:', error);
      throw error;
    }
  }
}

